/**
 * shared/ui/use-toast — Toast state store for shadcn/ui toast components.
 *
 * Standard shadcn/ui pattern: a module-level store (memory state + a
 * listeners array) so `toast()` can be called from anywhere (event handlers,
 * mutation `onError`/`onSuccess` callbacks, etc.) and any mounted `useToast()`
 * subscriber — including the single <Toaster /> mounted at the app root — re-
 * renders with the new toast list. Per-component `useState` (the previous
 * implementation) does NOT work for this: each caller had its own isolated
 * toast list that nothing ever rendered.
 */

import { useEffect, useState } from 'react';

export type ToastVariant = 'default' | 'destructive';

export interface ToastData {
  id: string;
  title?: string;
  description?: string;
  variant?: ToastVariant;
  open: boolean;
}

export interface ToastInput {
  title?: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number;
}

/** Max number of toasts visible at once — older ones are dropped. */
const TOAST_LIMIT = 3;
/** How long a toast stays visible before it auto-dismisses. */
const DEFAULT_TOAST_DURATION = 5000;
/** Grace period between "dismissed" (exit animation starts) and actual removal
 * from state, so the Toast's exit transition (see toast.tsx `animate-out`
 * classes) has time to play. Intentionally short — the old code used
 * shadcn's default of 1_000_000ms, which meant toasts were never really
 * removed from memory. */
const TOAST_REMOVE_DELAY = 300;

interface State {
  toasts: ToastData[];
}

type Action =
  | { type: 'ADD_TOAST'; toast: ToastData }
  | { type: 'DISMISS_TOAST'; toastId?: string }
  | { type: 'REMOVE_TOAST'; toastId?: string };

let idCounter = 0;
function generateId(): string {
  idCounter = (idCounter + 1) % Number.MAX_SAFE_INTEGER;
  return `toast-${idCounter}`;
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case 'DISMISS_TOAST':
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toastId || action.toastId === undefined
            ? { ...t, open: false }
            : t
        ),
      };

    case 'REMOVE_TOAST':
      if (action.toastId === undefined) {
        return { ...state, toasts: [] };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };

    default:
      return state;
  }
}

let memoryState: State = { toasts: [] };
const listeners: Array<(state: State) => void> = [];

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => listener(memoryState));
}

const removeTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
const dismissTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

function clearTimeoutFor(map: Map<string, ReturnType<typeof setTimeout>>, id: string) {
  const existing = map.get(id);
  if (existing) {
    clearTimeout(existing);
    map.delete(id);
  }
}

/** Sets `open: false` (starts the exit animation) then removes the toast
 * from state shortly after so it stops being rendered. */
function dismiss(toastId?: string) {
  dispatch({ type: 'DISMISS_TOAST', toastId });

  const idsToRemove = toastId !== undefined ? [toastId] : memoryState.toasts.map((t) => t.id);
  idsToRemove.forEach((id) => {
    clearTimeoutFor(removeTimeouts, id);
    const timeout = setTimeout(() => {
      removeTimeouts.delete(id);
      dispatch({ type: 'REMOVE_TOAST', toastId: id });
    }, TOAST_REMOVE_DELAY);
    removeTimeouts.set(id, timeout);
  });
}

/**
 * Imperatively create a toast. Callable from anywhere — component bodies,
 * event handlers, or TanStack Query `onError`/`onSuccess` callbacks.
 */
function toast(input: ToastInput) {
  const id = generateId();
  const { title, description, variant = 'default', duration = DEFAULT_TOAST_DURATION } = input;

  dispatch({
    type: 'ADD_TOAST',
    toast: { id, title, description, variant, open: true },
  });

  clearTimeoutFor(dismissTimeouts, id);
  const timeout = setTimeout(() => {
    dismissTimeouts.delete(id);
    dismiss(id);
  }, duration);
  dismissTimeouts.set(id, timeout);

  return {
    id,
    dismiss: () => dismiss(id),
  };
}

/**
 * Subscribes the calling component to the shared toast store.
 * Returns the current toast list plus `toast`/`dismiss` actions.
 */
function useToast() {
  const [state, setState] = useState<State>(memoryState);

  useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, []);

  return {
    toasts: state.toasts,
    toast,
    dismiss,
  };
}

export { useToast, toast };
